import React, { useEffect, useState } from "react";
import { Gauge, AlertTriangle, Coins, CheckCircle2, Clock, Megaphone, TrendingUp, Shield } from "lucide-react";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

// Mars Orange is locked for SLA warnings regardless of active theme
const MARS_ORANGE = "#FF4500";

export default function AffiliateDashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [kpi, setKpi] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [m, l] = await Promise.all([
          api.get("/affiliate/me"),
          api.get("/affiliate/ledger"),
        ]);
        setKpi(m.data.kpi);
        setLedger(l.data.items || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!user) return null;

  const progressPercent = kpi ? Math.min(100, Math.max(0, kpi.progress_percent || 0)) : 0;
  const underPerforming = kpi?.trending_below;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
          <Megaphone className="h-3 w-3 theme-text" /> Affiliate commander · Marketing publisher
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          {user.name || "Affiliate"} · <span style={{ color: theme.color }}>{user.affiliate_code || "—"}</span>
        </h1>
        <p className="text-zinc-400 mt-2">
          Read-only view of your link&apos;s metrics. B2B and investor data are walled off.
        </p>

        {/* SLA WARNING */}
        {underPerforming && !loading && (
          <div
            className="mt-8 p-5 border flex items-start gap-4"
            style={{
              borderColor: MARS_ORANGE,
              backgroundColor: "rgba(255, 69, 0, 0.08)",
              boxShadow: `0 0 30px ${MARS_ORANGE}44`,
            }}
            data-testid="sla-warning"
            role="alert"
          >
            <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5" style={{ color: MARS_ORANGE }} />
            <div>
              <div className="font-display font-black text-lg" style={{ color: MARS_ORANGE }}>
                SLA warning — trending below weekly target
              </div>
              <p className="text-sm text-zinc-300 mt-1">
                Blocks this week: <span className="font-mono font-bold">{kpi?.blocks_this_week.toLocaleString()}</span> / {kpi?.weekly_target.toLocaleString()} target.
                If the shortfall persists, a <span className="font-bold" style={{ color: MARS_ORANGE }}>{kpi?.sla_penalty_percent}% liquid penalty</span> will be compounded against pending escrow.
              </p>
            </div>
          </div>
        )}

        {/* KPI Engine */}
        <div className="mt-8 grid md:grid-cols-4 gap-4" data-testid="affiliate-kpi">
          <KpiCard
            icon={<Gauge className="h-4 w-4" />}
            label="This week"
            value={kpi ? kpi.blocks_this_week.toLocaleString() : "…"}
            sub={kpi ? `of ${kpi.weekly_target.toLocaleString()} blocks` : ""}
            testid="kpi-this-week"
          />
          <KpiCard
            icon={<TrendingUp className="h-4 w-4" />}
            label="Progress"
            value={kpi ? `${kpi.progress_percent}%` : "…"}
            sub={underPerforming ? "Below pace" : "On pace"}
            subColor={underPerforming ? MARS_ORANGE : theme.color}
            testid="kpi-progress"
          />
          <KpiCard
            icon={<Shield className="h-4 w-4" />}
            label="Total blocks sold"
            value={kpi ? kpi.total_blocks_sold.toLocaleString() : "…"}
            sub="all-time"
            testid="kpi-total"
          />
          <KpiCard
            icon={<Coins className="h-4 w-4" />}
            label="SLA risk"
            value={kpi ? `${kpi.sla_penalty_percent}%` : "0%"}
            sub={underPerforming ? "penalty armed" : "none armed"}
            subColor={underPerforming ? MARS_ORANGE : "#a1a1aa"}
            testid="kpi-sla"
          />
        </div>

        {/* Weekly progress bar */}
        <div className="mt-6 p-6 border border-white/10 bg-[#0A0A0A]" data-testid="weekly-progress">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Weekly block target</div>
              <div className="font-display font-bold text-lg mt-1">
                {kpi?.blocks_this_week.toLocaleString() ?? "…"} / {kpi?.weekly_target.toLocaleString() ?? "4,166"} blocks
              </div>
            </div>
            <div className="text-xs text-zinc-500 font-mono">{Math.round(progressPercent)}%</div>
          </div>
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${progressPercent}%`,
                backgroundColor: underPerforming ? MARS_ORANGE : theme.color,
                boxShadow: `0 0 12px ${underPerforming ? MARS_ORANGE : theme.color}`,
              }}
            />
          </div>
        </div>

        {/* Escrow Ledger */}
        <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid="escrow-summary">
          <div className="p-6 border border-white/10 bg-[#0A0A0A]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
              <Clock className="h-3.5 w-3.5 theme-text" /> Pending escrow
            </div>
            <div className="font-display font-black text-4xl theme-text" data-testid="escrow-pending">
              ${kpi?.pending_escrow_usd.toLocaleString() ?? "0"}
            </div>
            <p className="text-xs text-zinc-500 mt-2">Released to cleared once the weekly target is met.</p>
          </div>
          <div className="p-6 border border-white/10 bg-[#0A0A0A]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
              <CheckCircle2 className="h-3.5 w-3.5 theme-text" /> Cleared payouts
            </div>
            <div className="font-display font-black text-4xl" style={{ color: theme.color }} data-testid="escrow-cleared">
              ${kpi?.cleared_payouts_usd.toLocaleString() ?? "0"}
            </div>
            <p className="text-xs text-zinc-500 mt-2">All-time settled and transferred.</p>
          </div>
        </div>

        {/* Ledger rows */}
        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display font-bold text-xl">Escrow ledger</h2>
          <span className="text-sm text-zinc-500">{ledger.length} rows</span>
        </div>
        <div className="mt-4 border border-white/10 bg-[#0A0A0A]" data-testid="affiliate-ledger-table">
          {error ? (
            <p className="p-6 text-sm text-red-400">{error}</p>
          ) : loading ? (
            <p className="p-6 text-sm text-zinc-400">Loading…</p>
          ) : ledger.length === 0 ? (
            <p className="p-6 text-sm text-zinc-400">No sales yet — share your link to start moving blocks.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Buyer</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Blocks</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Amount USD</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Status</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Sold</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.map((row) => (
                  <TableRow key={row.id} className="border-white/5 hover:bg-white/5" data-testid={`ledger-row-${row.id}`}>
                    <TableCell className="font-medium text-sm">{row.buyer}</TableCell>
                    <TableCell className="text-zinc-300 font-mono text-sm">{row.blocks.toLocaleString()}</TableCell>
                    <TableCell className="text-zinc-300 font-mono text-sm">${row.amount_usd.toLocaleString()}</TableCell>
                    <TableCell>
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border"
                        style={{
                          borderColor: row.status === "cleared" ? theme.color : "rgba(255,255,255,0.15)",
                          color: row.status === "cleared" ? theme.color : "#a1a1aa",
                        }}
                      >
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-zinc-500 text-sm">{new Date(row.sold_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <p className="mt-8 text-xs text-zinc-600 text-center">
          Affiliate commander · read-only · B2B and Investor data are walled off
        </p>
      </main>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, subColor, testid }) {
  return (
    <div className="p-5 border border-white/10 bg-[#0A0A0A]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
        <span className="theme-text">{icon}</span> {label}
      </div>
      <div className="font-display font-black text-3xl tabular-nums">{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: subColor || "#a1a1aa" }}>{sub}</div>}
    </div>
  );
}
